import { useEffect } from 'react';
import { useSubjectLanguageVariants } from '@features/user';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import {
  useBookingRequestQuery,
  usePromoteProspectMutation,
  useProspectMatchesQuery,
} from '../store/api';

import {
  ReviewIdentityDialogView,
  type ReviewIdentitySelection,
} from './ReviewIdentityDialogView';

export interface ReviewIdentityDialogProps {
  bookingProspectId: string;
  open: boolean;
  onClose: () => void;
  /** Where this dialog was launched from — for analytics. */
  source: 'queue' | 'drawer';
  /** Called after a successful promotion. */
  onPromoted?: (patientId: string, createdNewPatient: boolean) => void;
}

/** Container for the identity-review step (ADR-0077 promotion).
 *
 * Fetches the prospect + ranked matches itself so it can be launched from
 * anywhere that knows a booking_prospect_id (queue card, detail drawer).
 */
export function ReviewIdentityDialog({
  bookingProspectId,
  open,
  onClose,
  source,
  onPromoted,
}: ReviewIdentityDialogProps) {
  const {
    data: prospectData,
    isLoading: isLoadingProspect,
    error: prospectError,
  } = useBookingRequestQuery({ bookingProspectId }, { skip: !open });
  const {
    data: matchesData,
    isLoading: isLoadingMatches,
    error: matchesError,
  } = useProspectMatchesQuery({ bookingProspectId }, { skip: !open });
  const [promoteProspect, { isLoading: isSaving }] = usePromoteProspectMutation();
  const { enqueueSnackbar } = useSnackbar();
  const { bookingRequests, error: errorTracking } = useEventTracking();
  const { singular, singularCap } = useSubjectLanguageVariants();

  const matches = (matchesData?.items ?? []).map(item => ({
    patientId: item.patient_id,
    name: item.name,
    dob: item.dob,
    email: item.email,
    phone: item.phone,
    confidence: item.confidence,
  }));

  useEffect(() => {
    if (open) {
      bookingRequests.reviewOpened({
        booking_prospect_id: bookingProspectId,
        source,
      });
    }
    // Deliberately only fires when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = async (selection: ReviewIdentitySelection) => {
    const createNew = 'createNew' in selection;
    bookingRequests.promoteSubmitted({
      booking_prospect_id: bookingProspectId,
      action: createNew ? 'create_new' : 'link_existing',
      match_count: matches.length,
      source,
    });
    try {
      const result = await promoteProspect({
        bookingProspectId,
        promoteProspectRequest: createNew
          ? { create_new: true }
          : { patient_id: selection.patientId },
      }).unwrap();
      enqueueSnackbar(
        result.created_new_patient
          ? `${singularCap} created and linked to the booking`
          : `Booking linked to the existing ${singular}`,
        { variant: 'success' },
      );
      onPromoted?.(result.patient_id, result.created_new_patient);
      onClose();
    } catch {
      enqueueSnackbar('Could not link the booking', { variant: 'error' });
      errorTracking.track('Could not link the booking', 'booking_promote');
    }
  };

  const prospect = prospectData?.prospect
    ? {
        firstName: prospectData.prospect.first_name,
        lastName: prospectData.prospect.last_name,
        email: prospectData.prospect.email,
        phone: prospectData.prospect.phone,
        dob: prospectData.prospect.dob,
        reason: prospectData.prospect.reason,
      }
    : null;

  return (
    <ReviewIdentityDialogView
      open={open}
      onClose={onClose}
      prospect={prospect}
      isLoading={isLoadingProspect || isLoadingMatches}
      loadFailed={Boolean(prospectError || matchesError)}
      matches={matches}
      isSaving={isSaving}
      onSubmit={handleSubmit}
      subjectSingular={singular}
    />
  );
}
